// Runtime-context ring buffers (issue #587, completing #575 gap 3): the last N console messages and
// the last N API calls, held in memory only, drained into a Brightmark feedback report at submit
// time.
//
// WHY: "it brings up no function" (#574) arrived with zero evidence of what happened on click. A
// report that carries the console errors and the failing request that fired at that moment is
// actionable without the reporter; one that doesn't is a guessing game.
//
// NDA / PII discipline — this is the whole design constraint:
//   * API calls record METHOD + PATH + STATUS + duration ONLY. Never a request body, never a
//     response body, never a query string (a search box's `?q=Jane+Smith` is member data), never a
//     header.
//   * Console arguments are serialised conservatively: strings, numbers, booleans and `Error`
//     (name + message) are kept; every other object is reduced to a bare type tag. A caller doing
//     `console.error("save failed", memberRecord)` therefore leaks a tag, not the record.
//   * Whatever survives that is run through {@link redactPii}, a client-side mirror of the backend
//     `context._sanitize_str` patterns (foreign emails, phone numbers, 7+ digit runs), so PII is
//     stripped BEFORE it leaves the browser. The server sanitises again on receipt
//     (defence-in-depth, same as the annotation strings).
//
// The buffers are process-local and never persisted — closing the tab discards them.

/** Console levels captured into the ring buffer. */
export type ConsoleLevel = "log" | "warn" | "error";

/** One captured console message (internal shape; drained as a formatted string). */
export interface ConsoleEntry {
  /** Monotonic capture order, shared with {@link ApiCallEntry} so a merge stays chronological. */
  seq: number;
  level: ConsoleLevel;
  /** `HH:MM:SS` local wall clock, for correlating with server logs. */
  time: string;
  /** Already redacted + capped. */
  text: string;
}

/** One captured API call. No bodies, no query string, no headers — by design. */
export interface ApiCallEntry {
  seq: number;
  /** Uppercased HTTP method. */
  method: string;
  /** Pathname only (origin, query and hash stripped). */
  path: string;
  /** HTTP status, or 0 when the request never completed (network error / abort). */
  status: number;
  /** Wall-clock duration in ms, rounded. */
  ms: number;
  time: string;
}

/** The report-ready snapshot handed to `captureClientHints()`. */
export interface TelemetrySnapshot {
  /** Formatted `HH:MM:SS [level] text` lines, oldest first. */
  console_errors: string[];
  /** Recent API calls, oldest first. */
  api_calls: { method: string; path: string; status: number; ms: number }[];
  /** The single most recent API call, or null when none was recorded. */
  last_api_call: { method: string; path: string; status: number } | null;
}

/** How many console entries are held in memory before eviction. */
export const CONSOLE_BUFFER_SIZE = 30;
/** How many console entries a report ships (the backend caps at the same number). */
export const CONSOLE_REPORT_SIZE = 20;
/** Per-entry character cap; the backend renders at most 200 chars per line. */
export const CONSOLE_ENTRY_MAX = 300;
/** How many API calls are held in memory before eviction. */
export const API_BUFFER_SIZE = 20;
/** How many API calls a report ships. */
export const API_REPORT_SIZE = 10;
/** Only same-origin calls under this prefix are recorded — this is API telemetry, not asset traffic. */
export const API_PATH_PREFIX = "/api/";
/**
 * Feedback's own endpoints are excluded: the unread-badge poll fires on a timer and would otherwise
 * evict every interesting call from a 20-slot buffer before the reporter finishes typing.
 */
export const API_PATH_EXCLUDED_PREFIX = "/api/feedback/";

const REDACTED = "[redacted]";

// Mirrors backend `apps/feedback/context.py` — keep the three patterns and their order in sync.
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_RE = /\+?\d[\d\s\-().]{7,}\d/g;
const LONG_DIGITS_RE = /\b\d{7,}\b/g;

/**
 * Strip PII shapes from a captured string: every email (the client has no notion of "the reporter's
 * own address" — the server re-runs this with that allowance), phone numbers, and runs of 7+ digits
 * (account/reference numbers). Short numeric ids (a 4-digit member id) survive deliberately: the
 * NDA guardrail is "member ids yes, member data no".
 */
export function redactPii(text: string): string {
  return text
    .replace(EMAIL_RE, REDACTED)
    .replace(PHONE_RE, REDACTED)
    .replace(LONG_DIGITS_RE, REDACTED);
}

/** `HH:MM:SS` for a Date, zero-padded, local time. */
function clockTime(at: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
}

/**
 * Serialise one console argument for capture. Deliberately lossy for objects: an arbitrary object
 * logged next to a message may be a member record, so it is reduced to a type tag rather than
 * JSON-serialised. `Error` is the one structured exception — its name + message IS the evidence.
 */
export function describeConsoleArg(arg: unknown): string {
  if (arg === null) return "null";
  if (arg === undefined) return "undefined";
  if (typeof arg === "string") return arg;
  if (typeof arg === "number" || typeof arg === "boolean" || typeof arg === "bigint") {
    return String(arg);
  }
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}`;
  }
  if (Array.isArray(arg)) return `[array(${arg.length})]`;
  if (typeof arg === "function") return "[function]";
  if (typeof arg === "object") {
    const name = (arg as { constructor?: { name?: string } }).constructor?.name;
    return name && name !== "Object" ? `[${name}]` : "[object]";
  }
  return "[value]";
}

/** Join console arguments into one capture line (see {@link describeConsoleArg} for the PII stance). */
export function formatConsoleArgs(args: unknown[]): string {
  return args.map(describeConsoleArg).join(" ").trim();
}

/**
 * Reduce a fetch/XHR target to the pathname we are willing to record, or null when the call is not
 * API traffic we want. Query string and hash are dropped wholesale (they can carry member data);
 * cross-origin calls are dropped (they are not this app's API).
 */
export function normalizeApiPath(target: string): string | null {
  if (!target) return null;
  let path: string;
  try {
    // A relative target resolves against the current document; an absolute one keeps its own origin.
    const base = typeof window !== "undefined" ? window.location.href : "http://localhost/";
    const url = new URL(target, base);
    if (typeof window !== "undefined" && url.origin !== window.location.origin) return null;
    path = url.pathname;
  } catch {
    return null;
  }
  if (!path.startsWith(API_PATH_PREFIX)) return null;
  if (path.startsWith(API_PATH_EXCLUDED_PREFIX)) return null;
  return path.slice(0, 200);
}

// --- the buffers -------------------------------------------------------------

let seqCounter = 0;
let consoleBuffer: ConsoleEntry[] = [];
let apiBuffer: ApiCallEntry[] = [];

/**
 * Record a console message. Eviction is level-aware: when the buffer is full the oldest `log` is
 * dropped first, so a chatty component can never push the actual errors out of a report.
 */
export function recordConsole(level: ConsoleLevel, text: string, at: Date = new Date()): void {
  const clean = redactPii(text).trim().slice(0, CONSOLE_ENTRY_MAX);
  if (!clean) return;
  consoleBuffer.push({ seq: ++seqCounter, level, time: clockTime(at), text: clean });
  while (consoleBuffer.length > CONSOLE_BUFFER_SIZE) {
    const oldestLog = consoleBuffer.findIndex((e) => e.level === "log");
    consoleBuffer.splice(oldestLog >= 0 ? oldestLog : 0, 1);
  }
}

/** Record one completed (or failed) API call. FIFO eviction — every entry is equally interesting. */
export function recordApiCall(
  call: { method: string; path: string; status: number; ms: number },
  at: Date = new Date(),
): void {
  apiBuffer.push({
    seq: ++seqCounter,
    method: (call.method || "GET").toUpperCase().slice(0, 10),
    path: call.path.slice(0, 200),
    status: Number.isFinite(call.status) ? Math.max(0, Math.min(599, Math.round(call.status))) : 0,
    ms: Number.isFinite(call.ms) ? Math.max(0, Math.round(call.ms)) : 0,
    time: clockTime(at),
  });
  while (apiBuffer.length > API_BUFFER_SIZE) apiBuffer.shift();
}

/** Drop everything captured so far (used by tests and by `uninstall`). */
export function resetTelemetry(): void {
  consoleBuffer = [];
  apiBuffer = [];
  seqCounter = 0;
}

/**
 * Choose which console entries a report ships when the buffer holds more than `limit`.
 *
 * NOT simply "the most recent N": errors and warnings are the evidence, `log` is context, and a
 * chatty render loop between the failure and the moment the reporter opens the modal would
 * otherwise push the actual error off the end. So the most recent errors/warnings are taken first,
 * any remaining slots are filled with the most recent logs, and the result is re-sorted into
 * capture order so it still reads like a log.
 */
export function selectConsoleEntries(entries: ConsoleEntry[], limit: number): ConsoleEntry[] {
  if (entries.length <= limit) return entries.slice();
  const kept = entries.filter((e) => e.level !== "log").slice(-limit);
  const room = limit - kept.length;
  const filled = room > 0 ? kept.concat(entries.filter((e) => e.level === "log").slice(-room)) : kept;
  return filled.sort((a, b) => a.seq - b.seq);
}

/**
 * The report-ready snapshot: up to {@link CONSOLE_REPORT_SIZE} console lines (see
 * {@link selectConsoleEntries} for which ones) and the most recent {@link API_REPORT_SIZE} API
 * calls, both **oldest first** so they read like a log (the backend caps from the end for the same
 * reason). Pure — draining does not clear the buffers, so a reporter who cancels and re-opens the
 * modal still gets the same evidence.
 */
export function snapshotTelemetry(): TelemetrySnapshot {
  const calls = apiBuffer.slice(-API_REPORT_SIZE);
  const last = apiBuffer[apiBuffer.length - 1];
  return {
    console_errors: selectConsoleEntries(consoleBuffer, CONSOLE_REPORT_SIZE).map(
      (e) => `${e.time} [${e.level}] ${e.text}`,
    ),
    api_calls: calls.map((c) => ({
      method: c.method,
      path: c.path,
      status: c.status,
      ms: c.ms,
    })),
    last_api_call: last ? { method: last.method, path: last.path, status: last.status } : null,
  };
}

// --- global instrumentation --------------------------------------------------

type ConsoleMethod = (...args: unknown[]) => void;
type FetchFn = typeof fetch;

/** Per-request state stashed on an XHR instance between `open()` and `loadend`. */
interface XhrMeta {
  __bmMethod?: string;
  __bmPath?: string | null;
  __bmStart?: number;
}

let uninstall: (() => void) | null = null;

const nowMs = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

/**
 * Patch `console.log/warn/error`, `window.fetch`, `XMLHttpRequest`, and the two global failure
 * events (`error`, `unhandledrejection`) so the ring buffers fill for the lifetime of the page.
 *
 * Idempotent (a second call is a no-op returning the first uninstaller) and fully reversible — the
 * returned function restores every original. Every patch delegates to the original FIRST and only
 * records afterwards inside a `try`, so a bug here can never break app behaviour.
 *
 * Call it once at app startup, AFTER any mock/service-worker layer installs its own fetch patch, so
 * this wrapper observes the response the app actually sees.
 */
export function installBrightmarkTelemetry(): () => void {
  if (uninstall) return uninstall;
  if (typeof window === "undefined") return () => {};

  const restores: (() => void)[] = [];

  // --- console ---
  const levels: ConsoleLevel[] = ["log", "warn", "error"];
  for (const level of levels) {
    const original = console[level] as ConsoleMethod;
    if (typeof original !== "function") continue;
    const patched: ConsoleMethod = (...args: unknown[]) => {
      original.apply(console, args);
      try {
        recordConsole(level, formatConsoleArgs(args));
      } catch {
        // never let telemetry break logging
      }
    };
    (console as unknown as Record<string, ConsoleMethod>)[level] = patched;
    restores.push(() => {
      (console as unknown as Record<string, ConsoleMethod>)[level] = original;
    });
  }

  // --- uncaught errors / rejections (the "clicked and nothing happened" evidence) ---
  const onError = (event: ErrorEvent): void => {
    try {
      recordConsole("error", `uncaught ${describeConsoleArg(event.error ?? event.message)}`);
    } catch {
      /* ignore */
    }
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    try {
      recordConsole("error", `unhandled rejection ${describeConsoleArg(event.reason)}`);
    } catch {
      /* ignore */
    }
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  restores.push(() => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  });

  // --- fetch ---
  const originalFetch: FetchFn | undefined = window.fetch?.bind(window);
  if (originalFetch) {
    const patchedFetch: FetchFn = async (input, init) => {
      let method = "GET";
      let path: string | null = null;
      try {
        const target =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.href
              : (input as Request).url;
        path = normalizeApiPath(target);
        method = (
          init?.method ??
          (typeof input === "object" && input !== null && "method" in input
            ? (input as Request).method
            : "GET")
        ).toUpperCase();
      } catch {
        path = null;
      }
      const started = nowMs();
      try {
        const response = await originalFetch(input, init);
        if (path) recordApiCall({ method, path, status: response.status, ms: nowMs() - started });
        return response;
      } catch (err) {
        // status 0 == the request never produced a response (offline, DNS, CORS, abort).
        if (path) recordApiCall({ method, path, status: 0, ms: nowMs() - started });
        throw err;
      }
    };
    window.fetch = patchedFetch;
    restores.push(() => {
      window.fetch = originalFetch;
    });
  }

  // --- XMLHttpRequest ---
  const Xhr = window.XMLHttpRequest;
  if (Xhr?.prototype) {
    const originalOpen = Xhr.prototype.open;
    const originalSend = Xhr.prototype.send;
    Xhr.prototype.open = function patchedOpen(
      this: XMLHttpRequest & XhrMeta,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      try {
        this.__bmMethod = String(method || "GET").toUpperCase();
        this.__bmPath = normalizeApiPath(typeof url === "string" ? url : url.href);
      } catch {
        this.__bmPath = null;
      }
      // `open` is overloaded (2-arg and 5-arg forms); forward whatever the caller passed.
      type OpenFn = (
        this: XMLHttpRequest,
        method: string,
        url: string | URL,
        ...rest: unknown[]
      ) => void;
      return (originalOpen as unknown as OpenFn).call(this, method, url, ...rest);
    } as typeof Xhr.prototype.open;
    Xhr.prototype.send = function patchedSend(
      this: XMLHttpRequest & XhrMeta,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      try {
        if (this.__bmPath) {
          this.__bmStart = nowMs();
          this.addEventListener("loadend", () => {
            try {
              recordApiCall({
                method: this.__bmMethod ?? "GET",
                path: this.__bmPath as string,
                status: this.status,
                ms: nowMs() - (this.__bmStart ?? nowMs()),
              });
            } catch {
              /* ignore */
            }
          });
        }
      } catch {
        /* ignore */
      }
      return originalSend.call(this, body);
    };
    restores.push(() => {
      Xhr.prototype.open = originalOpen;
      Xhr.prototype.send = originalSend;
    });
  }

  uninstall = () => {
    for (const restore of restores.reverse()) restore();
    uninstall = null;
    resetTelemetry();
  };
  return uninstall;
}

/** True when the global patches are currently installed (test/diagnostic helper). */
export function isTelemetryInstalled(): boolean {
  return uninstall !== null;
}
